import sentry from 'common/helpers/analytics/sentry'
import object from 'common/helpers/primitives/object'

import plural from './plural'
import matchLinkInText from './matchLinkInText'


const handleError = (err, extra, isThrow) => {
  err = `sb-react-intl: ${err}`

  sentry.captureException(err, { extra: { extra } }) // extra.extra to show separate object in "Additional Data"

  if (isThrow) {
    throw new Error(`${err}. ${object.stringify(extra)}`)
  }
  else {
    console.error(err, object.stringify(extra))
  }
}

/**
 * get array of formula values [ 'female {perfume}', 'male {cologne}', 'unisex {fragrance}' ]
 * reduce object of { female: 'perfume', male: 'cologne', unisex: 'fragrance' }
 *
 * @param {string} formula
 * @private
 */
const _getFormulaValues = (formula) =>
  formula
    .match(/[^\s]+ {[^}]*}/g)
    .reduce((obj, item) => {
      const [ match, key, value ] = item.match(/(.+) {(.*)}/)

      return { ...obj, [key]: value }
    }, {})

/**
 * Replace formulas with value based on passed values
 *
 * message: {count, plural, one {# product} other {# products}}
 * values: { count: 23 }
 *
 * result: 23 products
 *
 * @param {string} message
 * @param {Object} values
 * @param {string} locale
 * @returns {string} message
 */
const replacePluralFormulas = (message, values = {}, locale) => {
  const formulas = message.match(/{[^,{]+, plural.+?}}/g) // [ '{count, plural, one {# product} other {# products}}' ]

  if (formulas && formulas.length) {
    formulas.forEach((formula) => {
      try {
        const formulaValues = _getFormulaValues(formula)
        const valueKey      = formula.match(/[^,{]+/)[0] // count
        const value         = values[valueKey] // 23
        let formulaKey
        let result

        // if value not passed set pluralKey value from the formula (first item) - other
        if (typeof value === 'undefined') {
          handleError(`There is no looking value in passed values!`, { message, values, valueKey })

          formulaKey  = 'other' in formulaValues ? 'other' : Object.keys(formulaValues)[0] // eslint-disable-line
          result      = formulaValues[formulaKey] // # products
        }
        else {
          formulaKey  = plural[locale](value) // other
          result      = formulaValues[formulaKey].replace(/#/g, value) // 23 products
        }

        message = message.replace(formula, result)
      }
      catch (err) {
        handleError(err, { message, values }, true)
      }
    })
  }

  return message
}

/**
 * Replace formulas with value based on passed values
 *
 * message: {sex, select, female {perfume} male {cologne} unisex {fragrance}}
 * values: { sex: 'female' }
 *
 * result: perfume
 *
 * @param {string} message
 * @param {Object} values
 * @returns {string} message
 */
const replaceSelectFormulas = (message, values = {}) => {
  const formulas = message.match(/{[^,{]+, select.+?}}/g) // [ '{sex, select, female {perfume} male {cologne} unisex {fragrance}}' ]

  if (formulas && formulas.length) {
    formulas.forEach((formula) => {
      try {
        const formulaValues = _getFormulaValues(formula)
        const valueKey      = formula.match(/[^,{]+/)[0] // sex
        let formulaKey      = values[valueKey] // female

        // if value not passed set value from the formula (first item) - unisex
        if (typeof formulaKey === 'undefined') {
          handleError(`There is no looking value in passed values!`, { message, values, valueKey })
          formulaKey = Object.keys(formulaValues)[0] // eslint-disable-line
        }

        const result = (formulaValues[formulaKey] || '').replace(/#/g, formulaKey) // perfume

        message = message.replace(formula, result)
      }
      catch (err) {
        handleError(err, { message, values }, true)
      }
    })
  }

  return message
}

/**
 * Replace pure {value} variables
 *
 * message: Price - ${value}
 * values: { value: 100 }
 *
 * result: Price - $100
 *
 * @param {string} message
 * @param {Object} values
 * @returns {string} message
 */
const replaceVariables = (message, values = {}) =>
  // there is no way to math variables first and then iterate by them, coz if we try to find {variable} we match
  // variables in formulas - {sex, select, female {perfume} male {cologne} unisex {fragrance}}
  Object.keys(values).reduce((res, key) => (
    // prevent of replacing value in formulas (that matching the variable name)
    // example message - 'Some {key} with {check, plural, one {key} other {something}}'
    // const escapeFormulasRegexp = `{${key}}(?!([^{]+)?(({[^{}]+}([^{}]+)?)+)?})`
    //
    // here the ductape "() => values[key]" to fix Safari 11.* bug - '{value}'.replace(/{value}/g, '$14.95') becomes '4.95'
    res.replace(new RegExp(`{${key}}`, 'g'), () => values[key])
  ), message)

const stripSpaces = (message) => (
  message
    .replace(/\s{2,}/g, ' ')
    .replace(/<br\s?\/>\s+/g, '<br />')
    .replace(/{\s+/, '{')
    .replace(/\s+}/, '}')
    .trim()
)

const checkIfMessageHasVariables = (message) => /{[^}]+}/.test(message)

/**
 *
 * @param {Object} messageObj
 * @param {string} messageObj.en
 * @param {Object} [_values]
 * @param {string} locale
 */
const getMessage = (messageObj = {}, _values = {}, locale) => {
  let message = messageObj[locale] || messageObj.en

  // TODO this is ductape
  // to handle intl.formatMessage(title) where title contains { en: 'Title {value}', values: { value: 'foo' } }
  const values = messageObj.values || _values

  if (!message) {
    handleError('Missed translation', { values, messageObj })
    return '{{ missed_translation }}'
  }

  message = stripSpaces(message)

  if (!checkIfMessageHasVariables(message)) {
    return message
  }

  message = replaceVariables(message, values)

  if (!checkIfMessageHasVariables(message)) {
    return message
  }

  message = replaceSelectFormulas(message, values)

  if (!checkIfMessageHasVariables(message)) {
    return message
  }

  message = replacePluralFormulas(message, values, locale)

  if (!message) {
    handleError('Missed translation after formatting', { values })
    return '{{ missed_translation }}'
  }

  return message
}


class Intl {

  constructor() {
    this.locale = 'en'
  }

  setLocale(locale) {
    this.locale = locale
  }

  message(text, values = {}) {

    if (!text) {
      console.error('Empty text in intl.message()')
      return ''
    }

    return object.isObject(text) ? getMessage(text, values, this.locale) : text
  }

  /**
   *
   * @param {Object} messageObj
   * @param {string} messageObj.en
   * @param {Object} [values]
   */
  formatMessage(messageObj, values = {}) {
    return matchLinkInText(getMessage(messageObj, values, this.locale))
  }

  /**
   *
   * @param {Object} messageObj
   * @param {string} messageObj.en
   * @param {Object} [values]
   */
  formatHTMLMessage(messageObj, values = {}) {
    return getMessage(messageObj, values, this.locale)
  }
}


export default new Intl()
